import React, { useState, useMemo } from "react";
import {
  Plus,
  Minus,
  Package,
  Search,
  FileDown,
  FileText,
  ChevronDown,
  ChevronRight,
  Pencil,
  Save,
  Trash2,
  RotateCcw,
} from "lucide-react";

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StocktakeAddSplash from "@/components/drill/StocktakeAddSplash";
import {
  exportStocktakeCsv,
  exportStocktakePdf,
} from "@/components/drill/exportStocktake";

const HEADINGS = ["Bits", "Casing", "Consumables", "Muds", "Oils", "PPE", "Rig spares", "subs"].map(capitalize).sort((a,b)=>a.localeCompare(b));

export default function StocktakeSplash({
  open,
  onOpenChange,
  rigId,
  onRigIdChange,
  items,
  onAddItem,
  onAdjustQuantity,
  onEditItem,
  onRemoveItem,
  onClearQuantities,
  onSave,
  savedStocktakes,
  savedLoading,
  onLoadSaved,
  onDeleteSaved,
  onAddRecommendedToOrder,
  onAddCategoryToOrder,
  onAddItemToOrder,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => Object.fromEntries(HEADINGS.map((h) => [h, true])));
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savedOpen, setSavedOpen] = useState(false);

  const toggleCollapsed = (h) =>
    setCollapsed((p) => ({ ...p, [h]: !p[h] }));

  const visibleHeadings = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HEADINGS;
    return HEADINGS.filter((h) => {
      const list = items?.[h] || [];
      return (
        h.toLowerCase().includes(q) ||
        list.some((item) => {
          const name = typeof item === "string" ? item : item.name;
          return String(name).toLowerCase().includes(q);
        })
      );
    });
  }, [items, query]);

  const hasItems = useMemo(
    () => HEADINGS.some((h) => (items?.[h] || []).length > 0),
    [items]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-[calc(100vw-1rem)] max-w-4xl max-h-[calc(100dvh-1rem-env(safe-area-inset-bottom))] rounded-2xl overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-6 sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
        >
          <DialogHeader>
            <DialogTitle>Stocktake</DialogTitle>
            <DialogDescription>
              Log inventory items by category for this rig.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
            <div className="flex-1">
              <Label>Rig ID</Label>
              <Input
                value={rigId}
                onChange={(e) => onRigIdChange(e.target.value)}
                placeholder="e.g. RIG-04"
                className="rounded-xl"
              />
            </div>
            <Button
              onClick={() => setAddOpen(true)}
              className="rounded-xl bg-slate-900 hover:bg-slate-800"
            >
              Add
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search categories or items"
                className="rounded-xl pl-9"
              />
            </div>
            <div className="relative"><Button type="button" variant="outline" onClick={(e)=>{const el=e.currentTarget.nextElementSibling;el?.classList.toggle("hidden")}} disabled={!hasItems} className="rounded-xl gap-2 shrink-0"><FileDown className="w-4 h-4"/> Export <ChevronDown className="w-4 h-4"/></Button><div className="hidden absolute right-0 mt-2 z-20 w-32 rounded-xl border bg-white p-1 shadow-lg dark:bg-slate-900 dark:border-slate-700"><button className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100" onClick={()=>exportStocktakeCsv(rigId,items)}>CSV</button><button className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100" onClick={()=>exportStocktakePdf(rigId,items)}>PDF</button></div></div>
            <Button
              type="button"
              className="rounded-xl gap-2 bg-slate-900 hover:bg-slate-800 text-white shrink-0"
              onClick={() => {
                const stamp = new Date().toLocaleString(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
                });
                setSaveName(rigId ? `${rigId} ${stamp}` : `Stocktake ${stamp}`);
                setSaving(true);
              }}
            >
              <Save className="w-4 h-4" /> Save
            </Button>
            <Button
              type="button"
              className="rounded-xl gap-2 bg-slate-900 hover:bg-slate-800 text-white shrink-0"
              onClick={onAddRecommendedToOrder}
              disabled={!hasItems || !HEADINGS.some((h) => (items?.[h] || []).some((item) => Math.max(0, Number(item?.build_to || 0) - Number(item?.quantity || 0)) > 0))}
            >
              Add recommended items to order
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onClearQuantities?.()}
              disabled={!hasItems}
              className="rounded-xl gap-2 shrink-0"
            >
              <RotateCcw className="w-4 h-4" /> Clear
            </Button>
          </div>

          {saving && (
            <div className="rounded-2xl border border-slate-200 p-3 space-y-2 dark:border-slate-800">
              <Label>Stocktake name</Label>
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. RIG-04 July audit"
                className="rounded-xl"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => setSaving(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white"
                  disabled={!saveName.trim()}
                  onClick={() => {
                    onSave?.(saveName.trim());
                    setSaving(false);
                    setSaveName("");
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3 pt-1">
            {visibleHeadings.map((h) => {
              const list = items?.[h] || [];
              return (
                <div
                  key={h}
                  className="rounded-xl border border-slate-200 overflow-hidden dark:border-slate-800"
                >
                  <button
                    type="button"
                    onClick={() => toggleCollapsed(h)}
                    className="w-full px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-left select-none dark:bg-slate-800/50 dark:border-slate-800"
                  >
                    {collapsed[h] ? (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                    <Package className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                      {h}
                    </h3>
                    <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">{list.length}</span>
                    {list.length > 0 && <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={(e)=>{e.stopPropagation();onAddCategoryToOrder?.(h)}}>Add category to order</Button>}
                  </button>
                  {!collapsed[h] && (
                  <div className="px-4 py-3">
                    {list.length === 0 ? (
                      <p className="text-xs text-slate-400 dark:text-slate-500">
                        No items added.
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {list
                          .map((item, i) => ({ item, i }))
                          .sort((a, b) => {
                            const an = typeof a.item === "string" ? a.item : a.item.name;
                            const bn = typeof b.item === "string" ? b.item : b.item.name;
                            return String(an || "").localeCompare(
                              String(bn || ""),
                              undefined,
                              { sensitivity: "base" }
                            );
                          })
                          .map(({ item, i }) => {
                          const name = typeof item === "string" ? item : item.name;
                          const qty =
                            typeof item === "string" ? 1 : item.quantity ?? 0;
                          const buildTo =
                            typeof item === "string" ? undefined : item.build_to;
                          const recOrder = Math.max(0, (buildTo || 0) - (qty || 0));
                          return (
                            <li
                              key={i}
                              className="text-sm text-slate-700 flex items-start gap-2 dark:text-slate-200"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 mt-2" />
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditTarget({ heading: h, index: i })
                                    }
                                    className="flex-1 text-left flex items-center gap-1.5 group"
                                  >
                                    <span>{name}</span>
                                    <Pencil className="w-3 h-3 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400" />
                                  </button>
                              {onAdjustQuantity && (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onAdjustQuantity(h, i, -1)
                                    }
                                    className="w-6 h-6 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                  >
                                    <Minus className="w-3.5 h-3.5" />
                                  </button>
                                  <span className="w-7 text-center font-medium tabular-nums">
                                    {qty}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onAdjustQuantity(h, i, 1)
                                    }
                                    className="w-6 h-6 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                              </div>
                                <div className="flex items-center gap-3 mt-1.5 pl-5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-slate-400 dark:text-slate-500">Build to</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={buildTo ?? ""}
                                      onChange={(e) =>
                                        onEditItem?.(h, i, {
                                          build_to: e.target.value === "" ? undefined : +e.target.value,
                                        })
                                      }
                                      placeholder="—"
                                      className="h-7 w-16 rounded-lg text-xs"
                                    />
                                  </div>
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {item.uom ? `UOM: ${item.uom} · ` : ""}Recommended order: <span className="font-medium tabular-nums">{recOrder}</span>
                                  </span>
                                  <Button type="button" size="sm" variant="outline" className="h-7 rounded-lg text-[11px]" onClick={()=>onAddItemToOrder?.(h,i)} >Add to order</Button>
                                </div>
                                {Array.isArray(item.subs) &&
                                  item.subs.length > 0 && (
                                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                                      {item.subs
                                        .map(
                                          (s) =>
                                            [s.description, s.selection]
                                              .filter(Boolean)
                                              .join(" ")
                                        )
                                        .join(", ")}
                                    </p>
                                  )}
                                {item.bits &&
                                  [item.bits.thread, item.bits.size, item.bits.type]
                                    .some(Boolean) && (
                                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                                      {[
                                        item.bits.thread && `Thread: ${item.bits.thread}`,
                                        item.bits.size && `Size: ${item.bits.size}`,
                                        item.bits.type && `Type: ${item.bits.type}`,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </p>
                                  )}
                                {item.casing &&
                                  [item.casing.length, item.casing.type, item.casing.size]
                                    .some(Boolean) && (
                                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                                      {[
                                        item.casing.length && `Length: ${item.casing.length}`,
                                        item.casing.type && `Type: ${item.casing.type}`,
                                        item.casing.size && `Size: ${item.casing.size}`,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </p>
                                  )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden mb-2 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setSavedOpen((v) => !v)}
              className="w-full px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-left select-none dark:bg-slate-800/50 dark:border-slate-800"
            >
              {savedOpen ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                Saved Stocktakes
              </h3>
              <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
                {savedStocktakes?.length || 0}
              </span>
            </button>
            {savedOpen && (
              <div className="px-4 py-3 space-y-2">
                {savedLoading && (savedStocktakes || []).length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Loading…
                  </p>
                ) : (savedStocktakes || []).length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    No saved stocktakes yet.
                  </p>
                ) : (
                  (savedStocktakes || []).map((s) => (
                    <div
                      key={s.id}
                      className="flex flex-wrap items-center gap-2 text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-700 truncate dark:text-slate-200">
                          {s.name}
                        </div>
                        <div className="text-xs text-slate-400 dark:text-slate-500">
                          {s.rig_id ? `Rig ${s.rig_id} · ` : ""}
                          {s.created_date
                            ? new Date(s.created_date).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl h-9 px-3"
                        onClick={() => onLoadSaved?.(s)}
                      >
                        Load
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl h-9 px-3 text-red-600 border-red-200 dark:border-red-900/50"
                        onClick={() => onDeleteSaved?.(s.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <StocktakeAddSplash
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdd={(heading, name, quantity, subs, bits, casing, buildTo, uom) => {
          onAddItem(heading, name, quantity, subs, bits, casing, buildTo, uom);
          setAddOpen(false);
        }}
      />

      <StocktakeAddSplash
        open={Boolean(editTarget)}
        onOpenChange={(o) => !o && setEditTarget(null)}
        initial={
          editTarget
            ? (() => {
                const it = (items[editTarget.heading] || [])[editTarget.index];
                const name = typeof it === "string" ? it : it?.name;
                const quantity =
                  typeof it === "string" ? 1 : it?.quantity ?? 0;
                const subs = typeof it === "string" ? [] : it?.subs || [];
                const bits = typeof it === "string" ? {} : it?.bits || {};
                const casing = typeof it === "string" ? {} : it?.casing || {};
                const build_to = typeof it === "string" ? undefined : it?.build_to;
                const uom = typeof it === "string" ? "" : it?.uom || "";
                return {
                  heading: editTarget.heading,
                  name,
                  quantity,
                  subs,
                  bits,
                  casing,
                  build_to,
                  uom,
                };
              })()
            : undefined
        }
        onEdit={(heading, name, quantity, subs, bits, casing, buildTo, uom) => {
          onEditItem?.(editTarget.heading, editTarget.index, {
            name,
            quantity,
            subs,
            bits,
            casing,
            build_to: buildTo,
            uom,
          });
          setEditTarget(null);
        }}
        onDelete={() => {
          onRemoveItem?.(editTarget.heading, editTarget.index);
          setEditTarget(null);
        }}
      />
    </>
  );
}